const palette = [
	"#739bc8",
	"#64ad99",
	"#e3b75e",
	"#b18acb",
	"#d88787",
	"#72acbf",
	"#a4b66d",
];
export const typeKey = (type) =>
	type.unclassified
		? "__unclassified"
		: "type:" + type.type.trim().toLocaleLowerCase("it");
const typeHash = (key) =>
	Array.from(key).reduce(
		(hash, char) => (hash * 31 + char.charCodeAt(0)) >>> 0,
		0,
	);
export const typeColor = (type, colors) =>
	colors?.get(typeKey(type)) ||
	(type.unclassified
		? "#a0aab8"
		: palette[typeHash(typeKey(type)) % palette.length]);
const extraColor = (index) => {
	const hue = (index * 137.508) % 360,
		saturation = 0.6,
		lightness = 0.5;
	const a = saturation * Math.min(lightness, 1 - lightness);
	const channel = (n) => {
		const k = (n + hue / 30) % 12;
		return Math.round(
			255 * (lightness - a * Math.max(-1, Math.min(k - 3, 9 - k, 1))),
		)
			.toString(16)
			.padStart(2, "0");
	};
	return "#" + channel(0) + channel(8) + channel(4);
};
export function trainingTypeColors(analysis) {
	const summaries = [
		analysis?.period?.completed,
		analysis?.period?.scheduled,
		...(analysis?.months || []).flatMap((m) => [m.completed, m.scheduled]),
	];
	const types = Array.from(
		new Map(
			summaries.flatMap((s) => (s?.types || []).map((t) => [typeKey(t), t])),
		).values(),
	).sort((a, b) => typeKey(a).localeCompare(typeKey(b), "it"));
	const colors = new Map(),
		used = new Set(["#a0aab8"]);
	let extra = 0;
	for (const type of types) {
		if (type.unclassified) {
			colors.set(typeKey(type), "#a0aab8");
			continue;
		}
		const start = typeHash(typeKey(type)) % palette.length;
		let color = Array.from(
			{length: palette.length},
			(_, offset) => palette[(start + offset) % palette.length],
		).find((c) => !used.has(c));
		while (!color || used.has(color)) color = extraColor(extra++);
		used.add(color);
		colors.set(typeKey(type), color);
	}
	return colors;
}
export function monthlyTypes(analysis, scope, metric) {
	const months = analysis?.months || [];
	const types = Array.from(
		new Map(
			months.flatMap((month) =>
				(month[scope]?.types || []).map((type) => [typeKey(type), type]),
			),
		).values(),
	).sort(
		(a, b) =>
			Number(a.unclassified) - Number(b.unclassified) ||
			a.type.localeCompare(b.type, "it"),
	);
	const value = (summary, key) => {
		const type = summary?.types.find((row) => typeKey(row) === key);
		if (type) return metric === "minutes" ? type.minutes : type.sessions;
		return summary?.sessionsWithoutExercises > 0 ? null : 0;
	};
	return {
		types,
		rows: months.map((month) => ({
			month: month.month,
			summary: month[scope],
			...Object.fromEntries(
				types.map((type) => [
					typeKey(type),
					value(month[scope], typeKey(type)),
				]),
			),
		})),
	};
}

export function trainingTypeShare(value, total) {
	return value != null && total > 0 ? Math.round((100 * value) / total) : null;
}

export function trainingTypeValue(summary, type, metric) {
	const row = summary?.types.find((t) => typeKey(t) === typeKey(type));
	return row ? row[metric] : summary?.sessionsWithoutExercises > 0 ? null : 0;
}
export function trainingTypePartial(summary, type, metric) {
	const row = summary?.types.find((t) => typeKey(t) === typeKey(type));
	return (
		row?.unknownAttendance > 0 ||
		(metric === "minutes" && row?.missingDuration > 0)
	);
}
